import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('[Supabase] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
}

export const supabase: SupabaseClient = createClient(
  supabaseUrl || '',
  supabaseAnonKey || '',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
);

export const getFunctionsBaseUrl = () => {
  if (!supabaseUrl) return '';
  return `${supabaseUrl}/functions/v1`;
};

/**
 * Routing proxy result interface
 */
export interface RoutingProxyResult {
  minutes: number;
  distanceKm: number;
  source: 'here' | 'google' | 'osrm' | 'heuristic';
}

/**
 * Call the routing-proxy Edge Function to get travel time and distance
 * Proxies requests to HERE Maps, Google Maps, and OSRM with fallback chain
 * API keys are stored securely server-side (never exposed to client)
 * 
 * @param originLat - Origin latitude
 * @param originLng - Origin longitude
 * @param destLat - Destination latitude
 * @param destLng - Destination longitude
 * @param options - Optional parameters (transportMode, departureTime, fallbackEnabled)
 * @returns Promise with minutes, distanceKm, and source
 */
export const getRoutingViaProxy = async (
  originLat: number,
  originLng: number,
  destLat: number,
  destLng: number,
  options?: {
    transportMode?: 'car' | 'truck';
    departureTime?: string;
    fallbackEnabled?: boolean;
  }
): Promise<RoutingProxyResult | null> => {
  try {
    const { data, error } = await supabase.functions.invoke('routing-proxy', {
      body: {
        originLat,
        originLng,
        destLat,
        destLng,
        transportMode: options?.transportMode ?? 'car',
        departureTime: options?.departureTime,
        fallbackEnabled: options?.fallbackEnabled ?? true
      }
    });

    if (error) {
      console.error('[RoutingProxy] Edge Function Error:', error);
      return null;
    }

    if (data && typeof data.minutes === 'number' && typeof data.distanceKm === 'number') {
      return {
        minutes: data.minutes,
        distanceKm: data.distanceKm,
        source: data.source
      };
    }

    console.warn('[RoutingProxy] Invalid response:', data);
    return null;
  } catch (e) {
    console.error('[RoutingProxy] Exception:', e);
    return null;
  }
};
